import { Badge } from "@/components/ui/badge";
import { SupplierOrder } from "../types";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { formatCurrency } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface OrdersCellProps {
  orders: SupplierOrder[];
  supplierId: string;
  onUpdate: () => void;
}

const ORDER_STATUSES = {
  pending: { label: "En attente", color: "bg-blue-500" },
  delivered: { label: "Livré", color: "bg-green-500" },
  cancelled: { label: "Annulé", color: "bg-red-500" },
};

export const OrdersCell = ({ orders, supplierId, onUpdate }: OrdersCellProps) => {
  const handleStatusChange = async (orderId: string, newStatus: string) => {
    if (!orderId) {
      toast.error("ID de commande invalide");
      return;
    }

    const { error } = await supabase
      .from("supplier_orders")
      .update({ status: newStatus })
      .eq("id", orderId);

    if (error) {
      console.error("Error updating order status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
      return;
    }

    toast.success("Statut mis à jour avec succès");
    onUpdate();
  };

  if (!orders?.length) return "-";

  return (
    <HoverCard>
      <HoverCardTrigger>
        <div className="flex gap-2">
          {Object.entries(ORDER_STATUSES).map(([status, config]) => {
            const count = orders.filter(order => order.status === status).length;
            if (count === 0) return null;
            return (
              <Badge key={status} variant="default" className={`${config.color} cursor-pointer`}>
                {count}
              </Badge>
            );
          })}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-96">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Commandes</h4>
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="flex items-center justify-between py-1 border-b last:border-0">
                <div className="flex flex-col">
                  <span className="text-sm">Commande #{order.order_number}</span>
                  <span className="text-sm font-medium">{formatCurrency(order.total_amount)}</span>
                  {order.status === 'pending' && order.expected_delivery_date && (
                    <span className="text-xs text-muted-foreground">
                      Livraison prévue: {format(new Date(order.expected_delivery_date), 'Pp', { locale: fr })}
                    </span>
                  )}
                  {order.status === 'delivered' && order.created_at && (
                    <span className="text-xs text-muted-foreground">
                      Livré le: {format(new Date(order.created_at), 'Pp', { locale: fr })}
                    </span>
                  )}
                </div>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                  className="text-sm border rounded px-2 py-1"
                >
                  {Object.entries(ORDER_STATUSES).map(([value, { label }]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};